/*
 * <nl-w2-tabs> (sections/nl-w2-tabs-rail.liquid)
 *   [data-w2-tablist] > button[data-w2-tab][role=tab][aria-controls][data-url] + span[data-w2-ink]
 *   [data-w2-panel] (role=tabpanel, hidden when inactive) > <scroll-rail id> > [data-rail-track]
 *   <nl-w2-rail-nav> (assets/nl-w2-rail.js) gets the active rail on every change
 *   a[data-w2-view-all] follows the active collection
 *
 * ARIA tabs with automatic activation (arrows, Home, End). The ink glides with one transform; panels cross fade
 * in one grid cell (WAAPI, opacity + transform) and the first cards of the new panel arrive with a short stagger.
 * Reduced motion: instant switch. Theme editor: selecting a tab block activates it.
 * Emits `w2-tabs:change` { index, tab, panel } (bubbles).
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-tabs')) return;

  const EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
  const reduced = () =>
    (window.NL && window.NL.motion && window.NL.motion.reduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class W2Tabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.panels = Array.from(this.querySelectorAll('[data-w2-panel]'));
      if (!this.panels.length) return;
      this._bound = true;
      this.tabs = Array.from(this.querySelectorAll('[data-w2-tab]'));
      this.list = this.querySelector('[data-w2-tablist]');
      this.ink = this.querySelector('[data-w2-ink]');
      this.viewAll = this.querySelector('[data-w2-view-all]');
      this.nav = this.querySelector('nl-w2-rail-nav');
      this.index = Math.max(
        0,
        this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, true));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
      });

      if (this.list && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.placeInk(false));
        this.resizeObserver.observe(this.list);
      }

      this.onBlockSelect = (event) => {
        const tab = event.target && event.target.closest ? event.target.closest('[data-w2-tab]') : null;
        const i = this.tabs.indexOf(tab);
        if (i >= 0) this.select(i, false);
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      customElements.whenDefined('nl-w2-rail-nav').then(() => this.bindNav());

      this.placeInk(false);
      // Fonts change the tab widths: place the ink again once they are ready
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.placeInk(false));
      requestAnimationFrame(() => this.setAttribute('data-ready', ''));
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onBlockSelect) this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.finishSwitch();
      this._bound = false;
    }

    get activePanel() {
      return this.panels[this.index] || null;
    }

    bindNav() {
      const panel = this.activePanel;
      if (!this.nav || !panel) return;
      this.nav.rail = panel.querySelector('scroll-rail');
    }

    placeInk(animate) {
      const tab = this.tabs[this.index];
      if (!this.ink || !tab) return;
      const transform = `translate3d(${tab.offsetLeft}px, 0, 0) scaleX(${tab.offsetWidth})`;
      if (animate && !reduced()) {
        this.ink.style.transform = transform;
        return;
      }
      const transition = this.ink.style.transition;
      this.ink.style.transition = 'none';
      this.ink.style.transform = transform;
      void this.ink.offsetWidth;
      this.ink.style.transition = transition;
    }

    revealTab() {
      const tab = this.tabs[this.index];
      if (!this.list || !tab) return;
      const list = this.list;
      if (list.scrollWidth - list.clientWidth <= 2) return;
      const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: reduced() ? 'auto' : 'smooth' });
    }

    syncLink() {
      const tab = this.tabs[this.index];
      if (this.viewAll && tab && tab.dataset.url) this.viewAll.setAttribute('href', tab.dataset.url);
    }

    finishSwitch() {
      if (this.outAnimation) {
        this.outAnimation.onfinish = null;
        this.outAnimation.cancel();
        this.outAnimation = null;
      }
      if (this.leaving) {
        this.leaving.hidden = true;
        this.leaving.removeAttribute('data-leaving');
        this.leaving = null;
      }
    }

    select(index, fromUser) {
      const target = this.panels[index];
      if (!target) return;
      if (index === this.index) {
        if (fromUser && this.tabs[index]) this.tabs[index].focus();
        return;
      }

      this.finishSwitch();
      const from = this.activePanel;
      this.index = index;

      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (fromUser && this.tabs[index]) this.tabs[index].focus({ preventScroll: true });

      target.hidden = false;
      const track = target.querySelector('[data-rail-track]');
      if (track) {
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = 0;
        track.style.scrollBehavior = '';
      }

      this.placeInk(true);
      this.revealTab();
      this.syncLink();
      this.bindNav();

      if (!from || reduced() || typeof target.animate !== 'function') {
        if (from) from.hidden = true;
      } else {
        this.leaving = from;
        from.setAttribute('data-leaving', '');
        this.outAnimation = from.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 240,
          easing: 'ease-out',
          fill: 'forwards',
        });
        this.outAnimation.onfinish = () => this.finishSwitch();

        target.animate(
          [
            { opacity: 0, transform: 'translate3d(0, 12px, 0)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 560, easing: EASE }
        );
        if (track) {
          Array.from(track.children)
            .slice(0, 6)
            .forEach((item, i) => {
              item.animate(
                [
                  { opacity: 0, transform: 'translate3d(32px, 0, 0)' },
                  { opacity: 1, transform: 'none' },
                ],
                { duration: 640, delay: 50 * i, easing: EASE, fill: 'backwards' }
              );
            });
        }
      }

      this.dispatchEvent(
        new CustomEvent('w2-tabs:change', {
          bubbles: true,
          detail: { index, tab: this.tabs[index] || null, panel: target },
        })
      );
    }

    onKeydown(event, index) {
      const last = this.tabs.length - 1;
      const map = {
        ArrowRight: index === last ? 0 : index + 1,
        ArrowLeft: index === 0 ? last : index - 1,
        Home: 0,
        End: last,
      };
      if (!(event.key in map)) return;
      event.preventDefault();
      this.select(map[event.key], true);
    }
  }

  customElements.define('nl-w2-tabs', W2Tabs);
})();
